import React, { useState } from "react";
import { ArenaParticipant } from "@/hooks/useLiveArenas";
import VideoBoothTile from "./VideoBoothTile";
import ExpandedVideoModal from "./ExpandedVideoModal";

interface BalloonAssignment {
  oderId: string;
  balloonNumber: number;
  balloonColor: string;
  popCount: number;
}

interface VideoBoothGridProps {
  participants: ArenaParticipant[];
  currentUserId?: string;
  adminId?: string;
  localStream: MediaStream | null;
  getBalloon: (userId: string) => BalloonAssignment;
  getPopCount: (userId: string) => number;
  popsRemaining: number;
  onPopBalloon: (userId: string) => void;
}

const VideoBoothGrid: React.FC<VideoBoothGridProps> = ({
  participants,
  currentUserId,
  adminId,
  localStream,
  getBalloon,
  getPopCount,
  popsRemaining,
  onPopBalloon,
}) => {
  const [expandedParticipant, setExpandedParticipant] = useState<ArenaParticipant | null>(null);

  // Debug logging
  console.log("[VideoBoothGrid] State:", {
    participantsCount: participants.length,
    currentUserId,
    hasLocalStream: !!localStream,
    streamId: localStream?.id,
    videoTracks: localStream?.getVideoTracks().map(t => ({ enabled: t.enabled, readyState: t.readyState })),
  });

  if (participants.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">🎈</div>
        <h3 className="font-semibold text-foreground mb-2">Waiting for others...</h3>
        <p className="text-sm text-muted-foreground">
          Invite friends to join this live booth!
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Floating Grid */}
      <div className="flex flex-wrap justify-center gap-6 py-4 px-2">
        {participants.map((participant, index) => {
          const isCurrentUser = participant.user_id === currentUserId;
          const isAdmin = participant.user_id === adminId;
          const balloon = getBalloon(participant.user_id);
          const popCount = getPopCount(participant.user_id);

          return (
            <VideoBoothTile
              key={participant.user_id}
              name={participant.profile?.name || "User"}
              photoUrl={participant.profile?.photo_url}
              stream={isCurrentUser ? localStream : null} // For now, only show local stream
              isCurrentUser={isCurrentUser}
              isAdmin={isAdmin}
              isMuted={participant.is_muted || false}
              balloonNumber={balloon.balloonNumber}
              balloonColor={balloon.balloonColor}
              popCount={popCount}
              canPop={!isCurrentUser}
              popsRemaining={isCurrentUser ? 0 : popsRemaining}
              floatDelay={index * 0.3}
              onPop={() => onPopBalloon(participant.user_id)}
              onExpand={() => setExpandedParticipant(participant)}
            />
          );
        })}
      </div>

      {/* Info Bar */}
      <div className="text-center text-xs text-muted-foreground mt-4">
        <p>
          {popsRemaining > 0 
            ? `You have ${popsRemaining} balloon pop${popsRemaining === 1 ? "" : "s"} remaining`
            : "You've used all your pops in this booth"
          }
        </p>
      </div>

      {/* Expanded Video Modal */}
      {expandedParticipant && (
        <ExpandedVideoModal
          isOpen={!!expandedParticipant}
          onClose={() => setExpandedParticipant(null)}
          participant={expandedParticipant}
          isCurrentUser={expandedParticipant.user_id === currentUserId}
          stream={expandedParticipant.user_id === currentUserId ? localStream : null}
          isAdmin={expandedParticipant.user_id === adminId}
        />
      )}
    </>
  );
};

export default VideoBoothGrid;
